import type { CartItem, DiscountType } from "@/cart/types/cart";

const MAX_PERCENTAGE = 100;

export const roundCurrencyDown = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.floor(value));
};

export const sanitizePercentage = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(MAX_PERCENTAGE, Math.max(0, value));
};

export const sanitizeFixedAmount = (value: number, maxAmount: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const rounded = roundCurrencyDown(value);
  return Math.min(Math.max(0, rounded), Math.max(0, roundCurrencyDown(maxAmount)));
};

export const computeDiscountAmount = (
  baseAmount: number,
  type: DiscountType,
  rawValue: number,
): number => {
  if (baseAmount <= 0 || rawValue <= 0) {
    return 0;
  }

  if (type === "percentage") {
    const percent = sanitizePercentage(rawValue) / 100;
    return roundCurrencyDown(Math.min(baseAmount, baseAmount * percent));
  }

  return sanitizeFixedAmount(rawValue, baseAmount);
};

export const getCartDiscountRatio = (
  subtotal: number,
  type: DiscountType,
  rawValue: number,
): number => {
  if (subtotal <= 0 || rawValue <= 0) {
    return 0;
  }

  if (type === "percentage") {
    return sanitizePercentage(rawValue) / 100;
  }

  const fixedAmount = sanitizeFixedAmount(rawValue, subtotal);
  if (fixedAmount === 0) {
    return 0;
  }
  return fixedAmount / subtotal;
};

export const getItemNetUnitAmount = (item: CartItem): number => {
  const discount = item.discountAmount ?? 0;
  return Math.max(0, item.unitAmount - discount);
};

export const getItemNetSubtotal = (item: CartItem): number => {
  return getItemNetUnitAmount(item) * item.quantity;
};

export const getItemOriginalSubtotal = (item: CartItem): number => {
  return item.unitAmount * item.quantity;
};

export const hasDiscountApplied = (item: CartItem): boolean => {
  return (item.discountAmount ?? 0) > 0;
};

export interface FixedDiscountAllocationResult {
  perUnitDiscounts: number[];
  undistributed: number;
}

export const allocateFixedCartDiscounts = (
  items: CartItem[],
  targetDiscount: number,
): FixedDiscountAllocationResult => {
  const perUnitDiscounts = items.map(() => 0);
  const subtotal = items.reduce((sum, item) => sum + getItemOriginalSubtotal(item), 0);
  const normalizedTarget = Math.min(roundCurrencyDown(targetDiscount), subtotal);

  if (items.length === 0 || subtotal <= 0 || normalizedTarget <= 0) {
    return { perUnitDiscounts, undistributed: Math.max(0, normalizedTarget) };
  }

  const ratio = normalizedTarget / subtotal;

  const entries = items.map((item, index) => {
    const rawPerUnit = Math.min(item.unitAmount, item.unitAmount * ratio);
    const basePerUnit = Math.max(0, Math.floor(rawPerUnit));
    const fraction = rawPerUnit - basePerUnit;
    return {
      index,
      basePerUnit,
      fraction,
      quantity: Math.max(0, item.quantity || 0),
      maxPerUnit: item.unitAmount,
    };
  });

  let distributed = entries.reduce((sum, entry) => sum + entry.basePerUnit * entry.quantity, 0);
  let remainder = normalizedTarget - distributed;

  if (remainder > 0) {
    const order = entries
      .filter((entry) => entry.quantity > 0 && entry.basePerUnit < entry.maxPerUnit)
      .sort((a, b) => {
        if (b.fraction !== a.fraction) {
          return b.fraction - a.fraction;
        }
        const unitB = items[b.index].unitAmount;
        const unitA = items[a.index].unitAmount;
        if (unitB !== unitA) {
          return unitB - unitA;
        }
        return a.index - b.index;
      });

    while (remainder > 0 && order.length > 0) {
      let progressed = false;

      for (const candidate of order) {
        if (remainder <= 0) {
          break;
        }
        if (candidate.basePerUnit >= candidate.maxPerUnit) {
          continue;
        }
        if (candidate.quantity <= 0) {
          continue;
        }
        if (remainder < candidate.quantity) {
          continue;
        }

        candidate.basePerUnit += 1;
        remainder -= candidate.quantity;
        progressed = true;
      }

      if (!progressed) {
        break;
      }
    }
  }

  entries.forEach((entry) => {
    perUnitDiscounts[entry.index] = Math.min(entry.basePerUnit, entry.maxPerUnit);
  });

  return {
    perUnitDiscounts,
    undistributed: Math.max(0, remainder),
  };
};
